import { useQuery } from "@tanstack/react-query";

import { Fragment } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { type CurveType } from "recharts/types/shape/Curve";
import { apiRequest } from "shared/api/apiRequest";
import { getSocietyDashboardIdContract } from "shared/api/contracts/society/societyId/dashboards/dashboardId";
import Spinner from "shared/components/common/Spinner";
import CustomTooltip from "shared/components/dashboard/CustomTooltip";
import { type ChartDataType } from "shared/components/dashboard/dashboardMapper";
import { useSocietySession } from "shared/context/SocietySessionContext";
import { useSocietyColorPalette } from "shared/hooks/useSocietyColorPalette";

const LineChartModuleType = ({ dashboardId }: { dashboardId: number }) => {
  const { societyId } = useSocietySession();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboardModule", dashboardId],
    queryFn: async () =>
      apiRequest({
        contract: getSocietyDashboardIdContract,
        inputs: {
          params: {
            societyId,
            dashboardId,
          },
        },
      }),
    refetchOnWindowFocus: false,
  });

  const { paletteList } = useSocietyColorPalette(true);

  if (isLoading) {
    return (
      <div className="col-span-6 flex h-50 w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!data) {
    return <></>;
  }

  const { dashboardModule, data: results } = data;

  return (
    <div className="h-50 flex flex-col">
      <div className="flex items-center justify-center text-center text-base">
        <a href="#" className="mb-2 inline-block text-xl font-semibold">
          {dashboardModule.title}
        </a>
      </div>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart width={500} height={300} data={results as ChartDataType}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip
              content={
                <CustomTooltip
                  allData={results as ChartDataType}
                  previousDataLabel="Change from previous year"
                />
              }
            />
            <Legend />
            {dashboardModule.setsConfig?.map(
              ({ dataKey, fill, stroke, type }, index) => {
                if (dataKey === "default") {
                  return <Fragment key={dataKey} />;
                }

                const lineFill =
                  fill ?? paletteList[index % paletteList.length];

                return (
                  <Line
                    key={dataKey}
                    dataKey={dataKey}
                    fill={lineFill}
                    stroke={stroke ?? lineFill}
                    type={type as CurveType}
                  />
                );
              },
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default LineChartModuleType;
